import React, { Component } from 'react';
import PropTypes from 'prop-types';
import dc, {d3, crossfilter} from 'dc';
import CoordinateBaseChart from './coordinateBaseChart';
import * as utilFunctions from './util/utilFunctions';
import * as chartsUtilFunctions from './util/chartsUtilFunctions';
 
class BarChart extends CoordinateBaseChart {
  constructor(props, context) {
    super(props, context); 
    this.state = utilFunctions.mergeState(this.state, {
      config: utilFunctions.mergeState(this.state.config, {
          gap: 10,
          horizontal: false,
          legend: false
        }),
    }); 
    this.state.type = "BAR";
    this.state.tooltipObjectClass = ".bar";
    this.state.gapFixOrdinal = 0; //for fixes
  }

  getChartTypeInstance() {
    if(this.props.stack) {
      return dc.barChart("#"+this.props.id);
    }
    else {
      return dc.compositeChart("#"+this.props.id);
    }
  }

  getChartCompositeInstance() {
    return dc.barChart(this.state.chart);
  }

    //only one category, more than one metric and discrete group (to use metrics as union rows)
  isCompositeSpecialCase() {
    if(this.props.dimension.reduceFunction.type == "DISCRETE_GROUP" 
      && "parameters" in this.props.dimension.reduceFunction
      && this.props.dimension.reduceFunction.parameters.labels.length == 1 
      && this.state.crossfilter.getMetrics().length > 1 
      && this.props.stack == false) {
      return true;
    }
    return false;
  }

  setSize() {
    if(this.state.config.horizontal == true) {
      this.state.chart
        .width(this.state.height)
        .height(this.state.width);
    }
    else {
      this.state.chart
        .width(this.state.width)
        .height(this.state.height);
    }    
  }

  setMetrics() {
    super.setMetrics();

    if(this.isCompositeSpecialCase() == true) {
      var emptyGroup = chartsUtilFunctions.empty_group;

      this.state.composeCharts = ([
        this.getChartCompositeInstance()
            .group(emptyGroup())        
            .valueAccessor(function(){return 0})
      ]).concat(this.state.composeCharts);

      this.state.composeCharts = this.state.composeCharts.concat([
        this.getChartCompositeInstance()
            .group(emptyGroup())        
            .valueAccessor(function(){return 0})
      ]);
    }

    if(this.props.stack) {
      this.state.chart
        .valueAccessor(this.state.crossfilter.getValueAccesor());
    }
    else {
      this.state.chart
        .compose(this.state.composeCharts);
    } 
  }

  setAxis() {
    super.setAxis();
    if(this.state.config.horizontal == true) {
      this.state.chart
        .useRightYAxis(true);
    }
  }

  _stylingAndFormat(chart)
  {
    chart
      .centerBar(true)
      .gap(this.state.config.gap);
  }

  stylingAndFormat() {
    super.stylingAndFormat();

    if(this.state.config.legend == true) {
        this.state.chart.legend(
          dc.legend()
            .y(this.state.height-20)
            .x(this.state.width-20)
            .autoItemWidth(true)
            .itemHeight(10)
            .gap(10)
            .horizontal(true)
            .legendText((function(d,i) { 
              return d.name; 
            }).bind(this))
        );
      }
    
    if(this.props.stack) {
      this._stylingAndFormat(this.state.chart);
    }
    else
    {
      for(var i=0;i<this.state.composeCharts.length;i++) {
        this._stylingAndFormat(this.state.composeCharts[i]);
      }
    }

    //Fixes
    if(this.props.dimension.reduceFunction.type == "DISCRETE_GROUP" || 
      (this.state.config.xAxis.autoRange.active==false && this.state.config.xAxis.autoRange.fixWidth==true)) {
      var categoriesArrayLength = this.state.discreteLabelsNumber;
      if("parameters" in this.props.dimension.reduceFunction && this.props.dimension.reduceFunction.type == "DISCRETE_GROUP") {
        categoriesArrayLength = this.props.dimension.reduceFunction.parameters.labels.length;
      }
      else if(this.state.config.xAxis.autoRange.active==false && this.state.config.xAxis.autoRange.fixWidth==true) {
        categoriesArrayLength = this.state.config.xAxis.autoRange.max - this.state.config.xAxis.autoRange.min;
      }
      //use for fix for composite barchat offset gap
      this.state.config.gap = this.state.config.gap/(Math.max(categoriesArrayLength-1,1));
      if(this.isCompositeSpecialCase() == true) {
        this.state.chart._rangeBandPadding(1); 
        this.state.gapFixOrdinal = 0;
      }
      else if(categoriesArrayLength > 1 && !this.props.stack) {
        this.state.chart._rangeBandPadding(1);   
        this.state.gapFixOrdinal = -(this.state.config.gap/categoriesArrayLength)/2;       
      }
      else if(categoriesArrayLength == 1 && this.props.stack) {    
        this.state.config.gap = 2;
        this.state.gapFixOrdinal = 2;
      }
      else if(categoriesArrayLength == 1) {  
        this.state.chart._rangeBandPadding(1);   
        this.state.config.gap = 2;
        this.state.gapFixOrdinal = 2;
      }
      else {
        this.state.gapFixOrdinal = (this.state.config.gap/categoriesArrayLength)/4;
      }

      if(this.props.stack) {
        this.state.chart
          .centerBar(false);
      }
    }

    if(!this.props.stack) {
      for(var i=0;i<this.state.composeCharts.length;i++) {
        this.state.composeCharts[i].gap(this.state.config.gap);
      }
    }
    else {
      this.state.chart
        .gap(this.state.config.gap);
    }
  } 

  postRenderOperations() {
    super.postRenderOperations();  

    if(this.isCompositeSpecialCase() == true) {
      var actualXAxis = this.state.chart.svg().selectAll(".axis.x"); 
      var axisWidth = actualXAxis.node().getBBox().width;
      var labels = this.state.crossfilter.getMetricsLabels().concat([" "]);
      labels = [""].concat(labels);
      var axisScale = d3.scale.ordinal()
        .domain(labels)
        .rangePoints([0, axisWidth]);
      var xAxis = d3.svg.axis().scale(axisScale);

      var auxAxis = this.state.chart.svg().append("g")
        .attr("class","axis x")
        .attr("transform",actualXAxis.attr("transform"))
        .call(xAxis);

      actualXAxis.remove();

      if(this.state.config.xAxis.interval != null && this.state.config.xAxis.interval>1) {
        var i = 0;
        var _thisAux = this;
        this.state.chart.svg().selectAll(".axis.x .tick").each(function(d) {
          if(i%_thisAux.state.config.xAxis.interval == 0) {
            d3.select(this).remove();
          }
          i++;
        }); 
      }
    } 


    if(this.state.config.legend == true) {
      var legendWidth = this.state.chart.svg().selectAll(".dc-legend").node().getBBox().width;
      this.state.chart.svg().selectAll(".dc-legend").attr("transform","translate("+(this.state.width-legendWidth-15)+",-5)");   
      this.state.chart.svg().selectAll(".dc-legend text")
       .style("font-size","11px")
       .attr('transform', 'translate(1,-2.5)');   
    }

    //fixes
    var chart = this.state.chart;

    if(this.state.config.horizontal == true) {
      var width = chart.select('svg').attr("width");
      var height = chart.select('svg').attr("height");
      d3.select(this.refs.containerDivDom.parentNode.parentNode).classed("horizontalChart",true);

      if(chart.svg().select('text.y-axis-label').empty() == false) {
        chart.svg().select('text.y-axis-label').attr("transform", chart.svg().select('text.y-axis-label').attr("transform")+",rotate(180)");
      }
      chart.svg().attr({"width":height});
      chart.svg().attr({"height":width});
    }

    var length = this.state.composeCharts.length;
    if(this.props.stack) {
      length = this.state.crossfilter.getMetrics().length;
    }

    for(var i=0;i<length;i++) {
      if(chart.selectAll("g._" + i + " .bar").empty() == false) {
        var bars = chart.selectAll("g._" + i + " .bar");
        var barWidth = 0;
        for(var j=0;j<bars[0].length;j++) {
          var bbox = bars[0][j].getBBox();
          if(bbox.height>0) {
            barWidth = bbox.width;
            break;
          }
        }
        var numberOfMetrics = length;
        var newWidth = Math.max(8,(barWidth - (5*(length-1)))/numberOfMetrics);
        var metricOffset = (+i)*(newWidth + 5) + this.state.gapFixOrdinal;
        
        if(this.props.stack) {
          metricOffset = this.state.gapFixOrdinal;
          newWidth = Math.max(8,barWidth);
          chart.selectAll(".stack._" + i + " .bar").attr({"width":newWidth});
        }

        if(this.isCompositeSpecialCase() == true) {
            var actualXAxis = this.state.chart.svg().selectAll(".axis.x"); 
            var axisWidth = actualXAxis[0][0].getBBox().width;
            var gap = (axisWidth / (numberOfMetrics - 1))-newWidth;

            metricOffset = (+i)*(newWidth + gap) - newWidth/4;
        }

        chart.selectAll(".sub._" + i + " .bar").attr({"width":newWidth});
        chart.selectAll("g._" + i).attr("transform", function (d) { return "translate(" + metricOffset + ", 0)"; });
      }

      if(this.state.config.horizontal == true) {
        chart.svg().select('g').attr("transform","rotate(90 0,0) translate(0,-" + chart.select('svg').attr("width") + ")");
      }
    }
  }

  preRenderOperations() {
    super.preRenderOperations();

    if(this.props.stack) {
      this.state.chart.rescale();
    }
    else {
      for(var i=0;i<this.state.composeCharts.length;i++) {
        this.state.composeCharts[i].xAxisLength = this.state.chart.xAxisLength;
        this.state.composeCharts[i].rescale();
      }
    }
  }
}


export default BarChart;


// WEBPACK FOOTER //
// ./source/client/components/common/graphs/crossfilterGraphs/barChart.jsx